import {
  ENTITY_ACCESS_RIGHTS,
  FILTER_KEYS,
  HTTP_STATUS_CODE,
  QUERY_STATUS,
  ROLE_MATCH_MODE,
  SCOPES,
  TAG_TYPES,
} from "constants/constants";
import _ from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  createMissionQuery,
  editMissionQuery,
  getLateCreatedMissionsByStaffIdQuery,
  getMissionByIdQuery,
  getMissionClosedLessFifteenDaysBeforeEndsQuery,
  getMissionsByFiltersQuery,
  getMissionsByWorkerIdQuery,
  getMissionBySlugQuery,
  editMissionsPartialWithBillsQuery,
  deleteMissionByIdQuery,
  generateMissionPPTQuery,
  getMissionsExcelFileQuery,
  missionQuoteQuery,
  generateAllMissionsPPTQuery,
  getCorrespondanceTimelineQuery,
  getLateCloseMissionByStaffIdQuery,
  getModalAccessForMissionsQuery,
  getTimelineQuery,
} from "./queries";
import { downloadFileQuery } from "api/settings/queries";
import { useUserContext } from "api/user/UserProvider";
import { downloadFile, withUserScopeCheck } from "utils/helper";
import { streamingDownload } from "utils/streamingDownload";
import { canUserPerformAction } from "access-roles/general-functions";
import {
  MISSION_ROLES,
  ROLE_GET_CORRESPONDANCE,
  ROLE_GET_TIMELINE,
  ROLE_TOOL_MANAGER_MISSION,
  ROLE_TOOL_MANAGER_WORKER,
} from "access-roles/roles";
import {
  formattedErrorsMessage,
  extractApiFieldErrorsFlat,
} from "utils/apiError";

const MissionContext = createContext({});

export const useMissionContext = () => useContext(MissionContext);

export const MissionProvider = ({ ...props }) => {
  const [timelineMissionsList, setTimelineMissionsList] = useState([]);
  const [timelineEntityId, setTimelineEntityId] = useState(null);
  const [lateStaffCloseMission, setLateStaffCloseMission] = useState([]);
  const [staffClosedFifteenDays, setStaffClosedFifteenDays] = useState([]);
  const [staffLateCreatedMission, setStaffLateCreatedMission] = useState([]);
  const [workerMissionsList, setWorkerMissionsList] = useState([]);
  const [correspondanceMissionsList, setCorrespondanceMissionsList] = useState(
    []
  );
  const [missionCreated, setMissionCreated] = useState({});
  const [missionsEdited, setMissionsEdited] = useState([]);
  const [missionsByFilters, setMissionsByFilters] = useState({});
  const [missionById, setMissionById] = useState({});
  const [displayMission, setDisplayMission] = useState(false);
  const [faesFilter, setFaesFilter] = useState(false);
  const [missionBySlug, setMissionBySlug] = useState({});
  const [missionCreatedStatus, setMissionCreatedStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [loadMissionStatus, setLoadMissionStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [editMissionStatus, setEditMissionStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [loadTimelineCorrespondance, setLoadTimelineCorrespondance] = useState(
    QUERY_STATUS.INITIAL
  );
  const [creationFileStatus, setCreationFileStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [deleteMissionStatus, setDeleteMissionStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [timelineQueryStatus, setTimelineQueryStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [generateAllMissionsPPTStatus, setGenerateAllMissionsPPTStatus] =
    useState(QUERY_STATUS.INITIAL);
  const [pptExportProgress, setPptExportProgress] = useState(0);
  const [missionsExcelFileStatus, setMissionsExcelFileStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [missionQuoteStatus, setMissionQuoteStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [clientsExcelFileStatus, setClientsExcelFileStatus] = useState(
    QUERY_STATUS.INITIAL
  );

  const [missionAccessRights, setMissionAccessRights] = useState([]);
  const [refresh, setRefresh] = useState(false);

  const { userScope, postUserFilters, userRoleList, setApiErrorResponse } =
    useUserContext();

  const generateAllMissionsPPT = useCallback(
    async ({ filters }) => {
      setGenerateAllMissionsPPTStatus(QUERY_STATUS.LOADING);
      setPptExportProgress(0);
      try {
        const response = await generateAllMissionsPPTQuery({
          filters,
          userScope,
        });
        if (!response?.uri) {
          throw new Error("La génération du zip de ppt a échoué.");
        }
        await streamingDownload({
          path: `/file/${response.uri}?scope=${userScope}`,
          fileName: "missions.zip",
          mimeType: "application/zip",
          onProgress: setPptExportProgress,
        });
        setGenerateAllMissionsPPTStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setGenerateAllMissionsPPTStatus(QUERY_STATUS.ERROR);
        console.error(
          "Erreur lors de la génération ou du téléchargement du zip de ppt:",
          error
        );
      }
    },
    [userScope]
  );

  const getMissionClientCSV = useCallback(
    async ({ filters, lang }) => {
      setClientsExcelFileStatus(QUERY_STATUS.LOADING);
      try {
        await streamingDownload({
          path: `/directory/search/missions-client/export?scope=${userScope}&search=${filters}&order=mission&lang=${lang}`,
          fileName: "export-clients.xlsx",
          mimeType:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        setClientsExcelFileStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setClientsExcelFileStatus(QUERY_STATUS.ERROR);
        console.error(
          "Erreur lors de la génération ou du téléchargement du fichier:",
          error
        );
      }
    },
    [userScope]
  );

  const getMissionQuote = useCallback(
    async ({ missionId, slug }) => {
      setMissionQuoteStatus(QUERY_STATUS.LOADING);
      try {
        const response = await missionQuoteQuery({ missionId, userScope });
        if (!response?.uri) {
          throw new Error("La génération du devis de mission a échoué.");
        }
        await downloadFile({
          file_id: response.uri,
          fileType:
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          fileName: `${slug}.docx`,
          downloadFileQuery,
          userScope,
        });
        setMissionQuoteStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setMissionQuoteStatus(QUERY_STATUS.ERROR);
        console.error(
          "Erreur lors de la génération ou du téléchargement du devis de mission:",
          error
        );
      }
    },
    [userScope]
  );

  const getModalAccessForMissions = useCallback(
    async ({ missionId }) => {
      try {
        const result = await getModalAccessForMissionsQuery({
          missionId,
          userScope,
        });

        return result;
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des droits d'accés des missions",
          error
        );
      }
    },
    [userScope]
  );

  const getMissionsExcelFile = useCallback(
    async ({ filters }) => {
      setMissionsExcelFileStatus(QUERY_STATUS.LOADING);
      try {
        const response = await getMissionsExcelFileQuery({
          filters,
        });
        if (!response?.uri) {
          throw new Error("La génération du fichier Excel a échoué.");
        }
        await streamingDownload({
          path: `/file/${response.uri}?scope=${userScope}`,
          fileName: "missions.xlsx",
          mimeType:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        setMissionsExcelFileStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setMissionsExcelFileStatus(QUERY_STATUS.ERROR);
        console.error(
          "Erreur lors de la génération ou du téléchargement du fichier:",
          error
        );
      }
    },
    [userScope]
  );

  const handleDownloadMissionPPT = useCallback(
    async ({ id, slug }) => {
      setCreationFileStatus(QUERY_STATUS.LOADING);
      setPptExportProgress(0);
      try {
        const response = await generateMissionPPTQuery({ id, userScope });
        if (!response?.uri) {
          throw new Error("La génération du PPT a échoué.");
        }
        await streamingDownload({
          path: `/file/${response.uri}?scope=${userScope}`,
          fileName: `${slug}.pptx`,
          mimeType:
            "application/vnd.openxmlformats-officedocument.presentationml.presentation",
          onProgress: setPptExportProgress,
        });
        setCreationFileStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        console.error(
          "Erreur lors de la génération ou du téléchargement du fichier:",
          error
        );
        setCreationFileStatus(QUERY_STATUS.ERROR);
      }
    },
    [userScope]
  );

  const getMissionBySlug = useCallback(
    async ({ slug }) => {
      setMissionAccessRights([]);
      setLoadMissionStatus(QUERY_STATUS.LOADING);
      try {
        const result = await getMissionBySlugQuery({
          slug,
          userScope: userScope,
        });
        const hasGetAccessRights = result?.access.includes(
          ENTITY_ACCESS_RIGHTS.GET
        );
        if (hasGetAccessRights) {
          setMissionAccessRights(result?.access);
          setMissionBySlug(result);
          setLoadMissionStatus(QUERY_STATUS.SUCCESS);
          return result;
        } else {
          setLoadMissionStatus(QUERY_STATUS.ERROR);
        }
      } catch (error) {
        if (error.response?.status === HTTP_STATUS_CODE.NOT_FOUND) {
          setLoadMissionStatus(QUERY_STATUS.NOT_FOUND);
        } else {
          setLoadMissionStatus(QUERY_STATUS.ERROR);
        }
        console.error(error);
      }
    },
    [userScope]
  );

  const getMissionsByFilters = useCallback(
    async ({
      page = 1,
      filters,
      filtersToSave = null,
      userScope = null,
      orderBy,
    }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: [
            MISSION_ROLES.ROLE_GET_MISSION_LIST,
            MISSION_ROLES.ROLE_READ_MISSION_LIST,
            ROLE_TOOL_MANAGER_MISSION,
          ],
          mode: ROLE_MATCH_MODE.OR,
        })
      ) {
        return;
      }

      if (!filters || _.isEmpty(filters)) {
        return;
      }

      try {
        setLoadMissionStatus(QUERY_STATUS.LOADING);
        if (filtersToSave) {
          const parsedFilters = JSON.parse(filtersToSave);
          const isFiltersEmpty = Object.keys(parsedFilters).length === 0;
          if (!isFiltersEmpty)
            await postUserFilters(
              {
                key: FILTER_KEYS.MISSION,
                value: filtersToSave,
              },
              FILTER_KEYS.MISSION
            );
        }

        const result = await getMissionsByFiltersQuery({
          page: page,
          filters: orderBy ? `${filters}&order_by=${orderBy}` : filters,
          userScope: userScope,
        });
        setLoadMissionStatus(QUERY_STATUS.SUCCESS);
        return setMissionsByFilters((old) =>
          _.isEmpty(old) || page === 1
            ? result || { content: [], page: 1, total_pages: 1 }
            : {
                ...old,
                page,
                total_pages: result?.total_pages || old?.total_pages,
                content: _.concat(old?.content || [], result?.content || []),
              }
        );
      } catch (error) {
        setLoadMissionStatus(QUERY_STATUS.ERROR);
        console.error(error);
      }
    },
    [userScope, userRoleList]
  );

  const missionsByWorkerIdRequiredRole =
    userScope === SCOPES.READ
      ? MISSION_ROLES.ROLE_READ_MISSION
      : MISSION_ROLES.ROLE_GET_MISSION;

  const getMissionsByWorkerId = useCallback(
    async ({ id }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: missionsByWorkerIdRequiredRole,
        })
      ) {
        return;
      }
      try {
        const result = await getMissionsByWorkerIdQuery({ id, userScope });
        setWorkerMissionsList(result);
      } catch (error) {
        console.error(error);
      }
    },
    [setWorkerMissionsList, userScope, userRoleList]
  );

  const resetTimeline = useCallback(() => {
    setTimelineMissionsList([]);
    setTimelineEntityId(null);
    setTimelineQueryStatus(QUERY_STATUS.LOADING);
  }, []);

  const getTimeline = useCallback(
    async ({ filters, isToolsManager, orderBy }) => {
      const { id, type, missionsOnly } = filters;
      const requiredRoles =
        type === TAG_TYPES.CSLT_STR && isToolsManager
          ? ROLE_TOOL_MANAGER_WORKER
          : ROLE_GET_TIMELINE;
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles,
        })
      ) {
        return;
      }
      if (type && id) {
        try {
          setTimelineMissionsList([]);
          setTimelineQueryStatus(QUERY_STATUS.LOADING);
          const result = await getTimelineQuery({
            type,
            id,
            isToolsManager,
            orderBy,
            userScope,
            missionsOnly,
          });
          setTimelineMissionsList(result);
          setTimelineEntityId(id);
          return setTimelineQueryStatus(QUERY_STATUS.SUCCESS);
        } catch (error) {
          setTimelineQueryStatus(QUERY_STATUS.ERROR);
          console.error(error);
        }
      }
    },
    [setTimelineMissionsList, userScope, userRoleList]
  );

  const getCorrespondanceTimeline = useCallback(
    async ({ worker_type, start_date, end_date, page = 1 }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: ROLE_GET_CORRESPONDANCE,
        })
      ) {
        return;
      }
      try {
        setLoadTimelineCorrespondance(QUERY_STATUS.LOADING);
        const result = await getCorrespondanceTimelineQuery({
          worker_type,
          start_date,
          end_date,
          page,
        });
        setCorrespondanceMissionsList((old) =>
          _.isEmpty(old.content)
            ? {
                ...old,
                total: result?.total_items,
                content: result?.content,
                page: 1,
              }
            : {
                ...old,
                page,
                content: _.concat(old?.content, result?.content),
              }
        );

        setLoadTimelineCorrespondance(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setLoadTimelineCorrespondance(QUERY_STATUS.ERROR);
        console.error(error);
      }
    },
    [setCorrespondanceMissionsList, userScope, userRoleList]
  );

  const getLateCloseMissionByStaffId = useCallback(
    async ({ id }) => {
      const result = await getLateCloseMissionByStaffIdQuery({
        id,
        userScope: userScope,
      });
      setLateStaffCloseMission(result);
    },
    [setLateStaffCloseMission, userScope]
  );

  const getLateCreatedMissionsByStaffId = useCallback(
    async ({ id }) => {
      const result = await getLateCreatedMissionsByStaffIdQuery({
        id,
        userScope,
      });
      setStaffLateCreatedMission(result);
    },
    [setStaffLateCreatedMission, userScope]
  );

  const getMissionClosedLessFifteenDaysBeforeEnds = useCallback(
    async ({ id }) => {
      setStaffClosedFifteenDays(
        await getMissionClosedLessFifteenDaysBeforeEndsQuery({
          id,
          userScope: userScope,
        })
      );
    },
    [setStaffClosedFifteenDays, userScope]
  );

  const createMission = useCallback(
    async (data) => {
      setMissionCreatedStatus(QUERY_STATUS.LOADING);
      setApiErrorResponse(null);
      return await withUserScopeCheck({
        callback: async () => {
          try {
            const response = await createMissionQuery({ data, userScope });
            if (response) {
              setMissionCreatedStatus(QUERY_STATUS.SUCCESS);
              setMissionCreated(response);
            }
          } catch (error) {
            setMissionCreatedStatus(QUERY_STATUS.ERROR);
            const errors = error.response?.data?.["bad-request"];
            if (errors) {
              setApiErrorResponse(extractApiFieldErrorsFlat(errors));
            }
            console.error(error);
          }
        },
        scope: userScope,
      });
    },
    [userScope]
  );

  const getMissionById = useCallback(
    async (id) => {
      try {
        const result = await getMissionByIdQuery({ id, userScope });
        setMissionById(result);
      } catch (error) {
        console.error(error);
      }
    },
    [userScope]
  );

  const editMissionById = useCallback(
    async ({ id, data }) => {
      setEditMissionStatus(QUERY_STATUS.LOADING);
      setApiErrorResponse(null);
      try {
        const editedMission = await editMissionQuery({
          id,
          data,
          userScope,
        });
        setMissionsByFilters((old) => ({
          ...old,
          missions: _.map(old?.missions, (elm) =>
            elm?.id === editedMission?.id ? editedMission : elm
          ),
        }));
        setMissionById(editedMission);
        setEditMissionStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setEditMissionStatus(QUERY_STATUS.ERROR);
        const errors = error.response?.data?.["bad-request"];
        if (errors) {
          setApiErrorResponse(extractApiFieldErrorsFlat(errors));
        }
        console.error(error);
      }
    },
    [missionsByFilters, userScope]
  );

  const editMissionsPartialWithBills = useCallback(
    async ({ data }) => {
      const requiredRole =
        userScope === SCOPES.ADMIN
          ? MISSION_ROLES.ROLE_ADMIN_MISSION_BILL
          : MISSION_ROLES.ROLE_UPDATE_MISSION_BILL;
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: requiredRole,
        })
      ) {
        return;
      }
      setEditMissionStatus(QUERY_STATUS.LOADING);
      setApiErrorResponse(null);
      try {
        const result = await editMissionsPartialWithBillsQuery({
          data,
          userScope,
        });
        setMissionsEdited(result?.missions);
        setEditMissionStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setEditMissionStatus(QUERY_STATUS.ERROR);
        const errors = error.response.data["bad-request"];
        const { missions: missionsErrors, bills: billsErrors } = errors;

        const billsErrorsWithMissionId = Object.fromEntries(
          Object.entries(billsErrors).map(([key, value]) => {
            const missionId = data?.find((e) =>
              e?.bills?.find(
                (e2) =>
                  `${e2?.mission_id}-${e2?.bill_year}-${e2?.bill_month}` === key
              )
            )?.mission_partial?.id;
            return [missionId, value];
          })
        );

        const formattedErrors = Object.entries({
          ...missionsErrors,
          ...billsErrorsWithMissionId,
        })?.map(([missionId, errors]) => ({
          missionId: Number(missionId),
          errors,
        }));

        setApiErrorResponse(
          formattedErrorsMessage({
            errors: formattedErrors,
          })
        );
        setMissionsEdited(error.response.data?.success);
        console.error(error);
      }
    },
    [userScope, userRoleList]
  );

  const deleteMissionById = useCallback(
    async (id) => {
      setDeleteMissionStatus(QUERY_STATUS.LOADING);
      setApiErrorResponse(null);
      try {
        await deleteMissionByIdQuery({ id, userScope });
        setDeleteMissionStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setDeleteMissionStatus(QUERY_STATUS.ERROR);
        const errors = error.response.data?.["bad-request"];
        setApiErrorResponse(
          formattedErrorsMessage({
            errors: { errors, missionId: id },
          })
        );
        console.error(error);
      }
    },
    [userScope]
  );

  const values = useMemo(
    () => ({
      setMissionsByFilters,
      generateAllMissionsPPT,
      setCreationFileStatus,
      creationFileStatus,
      refresh,
      setRefresh,
      editMissionsPartialWithBills,
      getMissionsByWorkerId,
      getLateCloseMissionByStaffId,
      getLateCreatedMissionsByStaffId,
      lateStaffCloseMission,
      getMissionClosedLessFifteenDaysBeforeEnds,
      staffClosedFifteenDays,
      staffLateCreatedMission,
      workerMissionsList,
      createMission,
      missionCreated,
      missionCreatedStatus,
      getMissionsByFilters,
      missionsByFilters,
      loadMissionStatus,
      missionById,
      setMissionById,
      getMissionById,
      editMissionById,
      editMissionStatus,
      setEditMissionStatus,
      setMissionCreatedStatus,
      displayMission,
      setDisplayMission,
      getMissionBySlug,
      missionBySlug,
      setMissionBySlug,
      setMissionCreated,
      faesFilter,
      setFaesFilter,
      deleteMissionById,
      correspondanceMissionsList,
      setCorrespondanceMissionsList,
      getCorrespondanceTimeline,
      loadTimelineCorrespondance,
      setLoadTimelineCorrespondance,
      handleDownloadMissionPPT,
      getMissionsExcelFile,
      getMissionQuote,
      userScope,
      getMissionClientCSV,
      missionAccessRights,
      setMissionAccessRights,
      getModalAccessForMissions,
      setLoadMissionStatus,
      deleteMissionStatus,
      setDeleteMissionStatus,
      missionsEdited,
      setMissionsEdited,
      timelineMissionsList,
      setTimelineMissionsList,
      timelineQueryStatus,
      setTimelineQueryStatus,
      timelineEntityId,
      getTimeline,
      resetTimeline,
      generateAllMissionsPPTStatus,
      setGenerateAllMissionsPPTStatus,
      pptExportProgress,
      missionsExcelFileStatus,
      setMissionsExcelFileStatus,
      missionQuoteStatus,
      setMissionQuoteStatus,
      clientsExcelFileStatus,
      setClientsExcelFileStatus,
    }),
    [
      editMissionStatus,
      getMissionClientCSV,
      getMissionQuote,
      getMissionsExcelFile,
      handleDownloadMissionPPT,
      faesFilter,
      setFaesFilter,
      getMissionsByWorkerId,
      getLateCloseMissionByStaffId,
      getLateCreatedMissionsByStaffId,
      lateStaffCloseMission,
      getMissionClosedLessFifteenDaysBeforeEnds,
      staffClosedFifteenDays,
      staffLateCreatedMission,
      workerMissionsList,
      createMission,
      missionCreated,
      missionCreatedStatus,
      getMissionsByFilters,
      missionsByFilters,
      loadMissionStatus,
      missionById,
      setMissionById,
      getMissionById,
      editMissionById,
      setEditMissionStatus,
      setMissionCreatedStatus,
      displayMission,
      setDisplayMission,
      getMissionBySlug,
      missionBySlug,
      setMissionCreated,
      refresh,
      correspondanceMissionsList,
      setCorrespondanceMissionsList,
      getCorrespondanceTimeline,
      loadTimelineCorrespondance,
      setLoadTimelineCorrespondance,
      userScope,
      missionAccessRights,
      setMissionAccessRights,
      getModalAccessForMissions,
      setLoadMissionStatus,
      deleteMissionStatus,
      setDeleteMissionStatus,
      missionsEdited,
      setMissionsEdited,
      timelineMissionsList,
      setTimelineMissionsList,
      timelineQueryStatus,
      setTimelineQueryStatus,
      timelineEntityId,
      getTimeline,
      resetTimeline,
      generateAllMissionsPPTStatus,
      pptExportProgress,
      missionsExcelFileStatus,
      missionQuoteStatus,
      clientsExcelFileStatus,
      creationFileStatus,
    ]
  );
  return <MissionContext.Provider value={values} {...props} />;
};
